import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { History, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import { analysisService } from '../../services/analysis.service';
import { useAnalysisStore } from '../../store/analysisStore';
import type { StoredAnalysisPayload } from '../../store/analysisStore';
import { formatDate, formatPrice, calculateTotalPages, getSymbolName, NO_SETUP_STATUS } from '@trading/shared';

interface AnalysisHistoryPanelProps {
  /** Symbol used by the toggle "show only this symbol". */
  currentSymbol: string;
  /** Latest analysis id — when it changes the panel reloads to surface it. */
  refreshKey?: string;
}

const PAGE_SIZE = 10;

export default function AnalysisHistoryPanel({ currentSymbol, refreshKey }: AnalysisHistoryPanelProps) {
  const { t } = useTranslation('analysis');
  const { loadAnalysis, currentAnalysis } = useAnalysisStore(
    useShallow((s) => ({ loadAnalysis: s.loadAnalysis, currentAnalysis: s.currentAnalysis }))
  );

  const [items, setItems] = useState<StoredAnalysisPayload[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [onlyCurrent, setOnlyCurrent] = useState(true);
  const [hideNoSetup, setHideNoSetup] = useState(true);

  const fetchPage = useCallback(async (p: number) => {
    setLoading(true);
    try {
      // Server doesn't filter by symbol — fetch a wider slice when filtering
      // client-side to keep page navigation usable.
      // hideNoSetup is passed as excludeNoSetup so server total and item count
      // both honour the filter, keeping the badge and totalPages accurate.
      const limit = onlyCurrent ? PAGE_SIZE * 5 : PAGE_SIZE;
      const res = await analysisService.getHistory(p, limit, hideNoSetup);
      setItems(res.items);
      setTotal(res.total);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [onlyCurrent, hideNoSetup]);

  useEffect(() => {
    fetchPage(page);
  }, [fetchPage, page, refreshKey]);

  const visible = items
    .filter((a) => !onlyCurrent || a.symbol === currentSymbol)
    .filter((a) => !hideNoSetup || a.status !== NO_SETUP_STATUS)
    .slice(0, PAGE_SIZE);

  const totalPages = calculateTotalPages(total, PAGE_SIZE);

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-text-muted" />
          <h3 className="font-semibold text-text-primary text-sm">{t('historyPanel.title')}</h3>
          <Badge size="sm">{onlyCurrent ? visible.length : total}</Badge>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={hideNoSetup}
              onChange={(e) => {
                setHideNoSetup(e.target.checked);
                setPage(1);
              }}
              className="w-3.5 h-3.5 rounded border-border-subtle bg-bg-tertiary"
            />
            {t('historyPanel.hideNoSetup')}
          </label>
          <label className="flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={onlyCurrent}
              onChange={(e) => {
                setOnlyCurrent(e.target.checked);
                setPage(1);
              }}
              className="w-3.5 h-3.5 rounded border-border-subtle bg-bg-tertiary"
            />
            {t('historyPanel.onlyCurrentSymbol', { symbol: getSymbolName(currentSymbol) })}
          </label>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-10 px-4 text-sm text-text-muted">
          {hideNoSetup
            ? t('historyPanel.emptyAllHidden')
            : onlyCurrent
              ? t('historyPanel.emptyForSymbol')
              : t('historyPanel.empty')}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-tertiary text-xs text-text-muted uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">{t('historyPanel.col.date')}</th>
                  <th className="text-left px-3 py-2 font-medium">{t('historyPanel.col.symbol')}</th>
                  <th className="text-left px-3 py-2 font-medium">{t('historyPanel.col.tf')}</th>
                  <th className="text-left px-3 py-2 font-medium">{t('historyPanel.col.direction')}</th>
                  <th className="text-right px-3 py-2 font-medium">{t('historyPanel.col.confidence')}</th>
                  <th className="text-right px-3 py-2 font-medium">{t('historyPanel.col.entry')}</th>
                  <th className="text-right px-3 py-2 font-medium">{t('historyPanel.col.rr')}</th>
                  <th className="text-left px-3 py-2 font-medium">{t('historyPanel.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((analysis) => {
                  const isNoSetup = analysis.status === NO_SETUP_STATUS;

                  // Resolve trade-setup direction first (LONG/SHORT), then fall
                  // back to the market trend (bullish/bearish/ranging) so that
                  // analyses without a valid trade setup still show a meaningful
                  // direction instead of a dash.
                  //
                  // `setup` is typed as required by AnalysisResult but the DB
                  // column is Json? — pending/failed rows return null at runtime.
                  // The unknown cast documents this intentional type gap.
                  const setupRaw = (analysis.setup as unknown as { direction?: string } | null);
                  const setupDir = (
                    setupRaw?.direction ?? analysis.direction ?? ''
                  ).toString().toUpperCase();

                  // Market trend lives in the `analysis` JSON column (also nullable
                  // for pending analyses before the AI response arrives).
                  const analysisRaw = (analysis.analysis as unknown as { trend?: string } | null);
                  const trend = analysisRaw?.trend ?? '';
                  const isActive = analysis.id === currentAnalysis?.id;

                  // Resolve a display label and icon from setup direction or trend.
                  let dirLabel = '';
                  let DirIcon: typeof TrendingUp | typeof TrendingDown | typeof Minus | null = null;
                  if (setupDir === 'LONG') {
                    dirLabel = t('historyPanel.direction.long');
                    DirIcon = TrendingUp;
                  } else if (setupDir === 'SHORT') {
                    dirLabel = t('historyPanel.direction.short');
                    DirIcon = TrendingDown;
                  } else if (trend === 'bullish') {
                    dirLabel = t('historyPanel.direction.bullish');
                    DirIcon = TrendingUp;
                  } else if (trend === 'bearish') {
                    dirLabel = t('historyPanel.direction.bearish');
                    DirIcon = TrendingDown;
                  } else if (trend === 'ranging') {
                    dirLabel = t('historyPanel.direction.ranging');
                    DirIcon = Minus;
                  }

                  return (
                    <tr
                      key={analysis.id}
                      onClick={() => loadAnalysis(analysis)}
                      className={`border-t border-border-subtle cursor-pointer transition-colors hover:bg-bg-tertiary ${
                        isActive ? 'bg-accent-primary/5' : ''
                      } ${isNoSetup ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-2.5 text-text-secondary whitespace-nowrap">
                        {formatDate(analysis.createdAt)}
                      </td>
                      <td className="px-3 py-2.5 text-text-primary font-medium">{getSymbolName(analysis.symbol)}</td>
                      <td className="px-3 py-2.5 text-text-muted">{analysis.timeframe}</td>
                      <td className="px-3 py-2.5">
                        {dirLabel && DirIcon ? (
                          <span className="inline-flex items-center gap-1 text-text-primary">
                            <DirIcon className="w-3.5 h-3.5 text-text-muted" />
                            {dirLabel}
                          </span>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right text-text-secondary tabular-nums">
                        {isNoSetup
                          ? <span className="text-text-muted">—</span>
                          : <>
                              {analysis.setup?.confidence ?? analysis.confidence ?? '—'}
                              {(analysis.setup?.confidence ?? analysis.confidence) != null && '%'}
                            </>
                        }
                      </td>
                      <td className="px-3 py-2.5 text-right text-text-secondary tabular-nums">
                        {!isNoSetup && (analysis.setup?.entry?.price != null
                          ? formatPrice(analysis.setup.entry.price)
                          : analysis.entry != null
                            ? formatPrice(analysis.entry)
                            : '—')}
                        {isNoSetup && <span className="text-text-muted">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right text-text-secondary tabular-nums">
                        {!isNoSetup && (analysis.setup?.riskReward ?? analysis.riskReward
                          ? `1:${(analysis.setup?.riskReward ?? analysis.riskReward)?.toFixed(2)}`
                          : '—')}
                        {isNoSetup && <span className="text-text-muted">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge
                          size="sm"
                          variant={analysis.status === 'completed' ? 'success' : 'default'}
                        >
                          {analysis.status === NO_SETUP_STATUS ? t('history.noSetup') : analysis.status}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!onlyCurrent && totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-border-subtle text-xs text-text-secondary">
              <span>
                {t('historyPanel.page', { page, totalPages })}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  className="p-1.5 rounded hover:bg-bg-tertiary disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label={t('historyPanel.prev')}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  className="p-1.5 rounded hover:bg-bg-tertiary disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label={t('historyPanel.next')}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
